// iter120 — Création ultra-rapide d'un OR Parc.
// iter121 — UX fixes :
//  - véhicule en saisie libre (plaque + marque/modèle, pas de création en base)
//  - section "Autres articles" en saisie libre (libellé + qté + unité)
//  - les inputs numériques acceptent la virgule (parse "," → ".")
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ArrowLeft, Save, Truck, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

// Parse un nombre tolérant à la virgule décimale (iOS clavier numérique FR)
const parseNum = (v) => {
  if (v === null || v === undefined || v === "") return NaN;
  return parseFloat(String(v).replace(",", "."));
};
// Sanitize un input numérique : autorise chiffres + . et ,
const sanitizeNum = (v) => String(v).replace(/[^0-9.,]/g, "").replace(/(,.*),/g, "$1").replace(/(\..*)\./g, "$1");

export default function OrParcNouveau() {
  const navigate = useNavigate();
  const [predefined, setPredefined] = useState([]);
  const [clientsParc, setClientsParc] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [clientId, setClientId] = useState("");
  // iter121 — Véhicule en saisie libre (pas de select, pas de création en collection vehicules)
  // iter132.f — Remplace `plaque` par `info` (texte libre : VIN, couleur, année, surnom…)
  // car les véhicules de parc sont souvent non plaqués.
  const [vehiculeLibre, setVehiculeLibre] = useState({ info: "", marque: "", modele: "" });
  const [km, setKm] = useState("");
  const [notes, setNotes] = useState("");
  const [articleQuantites, setArticleQuantites] = useState({}); // {key: quantite (string)}
  const [articlesLibres, setArticlesLibres] = useState([]); // [{libelle, quantite, unite}]
  const [moLignes, setMoLignes] = useState([{ libelle: "", heures: "" }]);
  const [submitting, setSubmitting] = useState(false);
  // iter128 — Intervenant principal auto-rempli depuis le user courant
  const [meIntervenant, setMeIntervenant] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/or-parc/predefined-articles").then((r) => r.data.articles),
      api.get("/clients?limit=500").then((r) => {
        const d = r.data;
        return Array.isArray(d) ? d : (d.items || []);
      }).catch(() => []),
      api.get("/atelier/me/intervenant-id").then((r) => r.data).catch(() => null),
    ]).then(([arts, cls, me]) => {
      setPredefined(arts);
      setClientsParc((cls || []).filter((c) => c.is_parc === true));
      setMeIntervenant(me);
      setLoading(false);
    });
  }, []);

  const updateArticleQ = (key, value) => {
    setArticleQuantites((s) => ({ ...s, [key]: sanitizeNum(value) }));
  };

  const addArticleLibre = () => setArticlesLibres((l) => [...l, { libelle: "", quantite: "", unite: "piece" }]);
  const updateArticleLibre = (i, field, value) => setArticlesLibres((l) => l.map((ln, idx) => idx === i ? { ...ln, [field]: field === "quantite" ? sanitizeNum(value) : value } : ln));
  const removeArticleLibre = (i) => setArticlesLibres((l) => l.filter((_, idx) => idx !== i));

  const addMoLine = () => setMoLignes((l) => [...l, { libelle: "", heures: "" }]);
  const updateMoLine = (i, field, value) => setMoLignes((l) => l.map((ln, idx) => idx === i ? { ...ln, [field]: field === "heures" ? sanitizeNum(value) : value } : ln));
  const removeMoLine = (i) => setMoLignes((l) => l.length > 1 ? l.filter((_, idx) => idx !== i) : l);

  const canSubmit = useMemo(() => {
    if (!clientId) return false;
    if (!(vehiculeLibre.info || "").trim() && !(vehiculeLibre.marque || "").trim() && !(vehiculeLibre.modele || "").trim()) return false;
    const nbArticles = Object.values(articleQuantites).filter((q) => parseNum(q) > 0).length;
    const nbLibres = articlesLibres.filter((a) => parseNum(a.quantite) > 0 && (a.libelle || "").trim()).length;
    const nbMo = moLignes.filter((ln) => parseNum(ln.heures) > 0 && (ln.libelle || "").trim()).length;
    return nbArticles + nbLibres + nbMo > 0;
  }, [clientId, vehiculeLibre, articleQuantites, articlesLibres, moLignes]);

  const handleSubmit = async (statutInitial = "commencement") => {
    if (!canSubmit) {
      toast.error("Sélectionne au moins un article ou une ligne MO");
      return;
    }
    setSubmitting(true);
    try {
      const articles = Object.entries(articleQuantites)
        .filter(([, q]) => parseNum(q) > 0)
        .map(([key, q]) => ({ key, quantite: parseNum(q) }));
      const articles_libres = articlesLibres
        .filter((a) => parseNum(a.quantite) > 0 && (a.libelle || "").trim())
        .map((a) => ({ libelle: a.libelle.trim(), quantite: parseNum(a.quantite), unite: a.unite || "piece" }));
      const mo_lignes = moLignes
        .filter((ln) => parseNum(ln.heures) > 0 && (ln.libelle || "").trim())
        .map((ln) => ({ libelle: ln.libelle.trim(), heures: parseNum(ln.heures) }));

      const { data } = await api.post("/or-parc", {
        client_id: clientId,
        vehicule_libre: {
          info: (vehiculeLibre.info || "").trim() || null,
          marque: (vehiculeLibre.marque || "").trim() || null,
          modele: (vehiculeLibre.modele || "").trim() || null,
        },
        km_depose: km ? parseInt(km, 10) : null,
        articles,
        articles_libres,
        mo_lignes,
        notes_internes: notes || null,
        statut_initial: statutInitial,
        intervenant_principal_id: meIntervenant?.id || null,  // iter128 — auto-assign du mécano qui crée
      });
      const label = statutInitial === "travaux_effectue" ? "terminé" : "en travaux";
      toast.success(`OR Parc ${data.numero} créé (${label})`);
      navigate(`/atelier/or/${data.or_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur création OR Parc");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-10 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#52a675]" /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-5" data-testid="or-parc-nouveau-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/atelier")} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="or-parc-back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Truck className="w-6 h-6 text-blue-600" />
            Nouvel OR Parc
          </h1>
          <p className="text-xs text-gray-500">Module ultra-rapide pour clients flotte. Pas de RDV ni de prise en charge.</p>
        </div>
        {/* iter128 — Badge "Créé par" auto-attribué (intervenant principal) */}
        {meIntervenant && (
          <div
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#52a675]/10 border border-[#52a675]/30 text-[#2d5f3f] text-xs font-medium"
            data-testid="or-parc-me-badge"
            title="Cet OR Parc sera automatiquement attribué à toi comme intervenant principal"
          >
            <UserCheck className="w-3.5 h-3.5" />
            Attribué à : <strong>{meIntervenant.label}</strong>
          </div>
        )}
      </div>

      {/* Client + véhicule libre */}
      <Card className="p-5 space-y-3">
        <h2 className="font-semibold text-gray-900">Client &amp; véhicule</h2>
        <div>
          <Label className="text-xs">Client Parc</Label>
          <select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            data-testid="or-parc-client-select"
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-base focus:border-[#52a675] focus:ring-1 focus:ring-[#52a675]/40 focus:outline-none"
            style={{ fontSize: 16 }}
          >
            <option value="">— Choisir —</option>
            {clientsParc.map((c) => (
              <option key={c.id} value={c.id}>
                {c.raison_sociale || `${c.prenom || ""} ${c.nom || ""}`.trim()}
              </option>
            ))}
          </select>
          {clientsParc.length === 0 && (
            <div className="text-[10px] text-orange-600 mt-1">
              Aucun client marqué « Parc ». Active le flag dans la fiche client.
            </div>
          )}
        </div>
        {/* iter121 — Véhicule en saisie libre */}
        {/* iter132.f — Champ "Info" libre (VIN, couleur, année, surnom…) à la place
            de "Plaque" car les véhicules de parc sont souvent non plaqués. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Marque</Label>
            <Input
              value={vehiculeLibre.marque}
              onChange={(e) => setVehiculeLibre({ ...vehiculeLibre, marque: e.target.value })}
              placeholder="Renault"
              data-testid="or-parc-veh-marque"
              style={{ fontSize: 16 }}
            />
          </div>
          <div>
            <Label className="text-xs">Type / modèle</Label>
            <Input
              value={vehiculeLibre.modele}
              onChange={(e) => setVehiculeLibre({ ...vehiculeLibre, modele: e.target.value })}
              placeholder="Kangoo"
              data-testid="or-parc-veh-modele"
              style={{ fontSize: 16 }}
            />
          </div>
          <div>
            <Label className="text-xs">Info</Label>
            <Input
              value={vehiculeLibre.info}
              onChange={(e) => setVehiculeLibre({ ...vehiculeLibre, info: e.target.value })}
              placeholder="VIN, couleur, année…"
              data-testid="or-parc-veh-info"
              style={{ fontSize: 16 }}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Kilométrage</Label>
            <Input
              value={km}
              onChange={(e) => setKm(e.target.value.replace(/\D/g, ""))}
              type="tel"
              data-testid="or-parc-km"
              style={{ fontSize: 16 }}
            />
          </div>
        </div>
      </Card>

      {/* Articles pré-définis */}
      <Card className="p-5 space-y-3">
        <h2 className="font-semibold text-gray-900">Articles courants</h2>
        <p className="text-xs text-gray-500">Saisis simplement la quantité utilisée. L&apos;admin complétera les prix avant facturation.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {predefined.map((a) => (
            <div key={a.key} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 border border-gray-200 rounded-lg hover:border-blue-300 transition">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-gray-900 truncate">{a.libelle}</div>
                <div className="text-[10px] text-gray-500">unité : {a.unite}</div>
              </div>
              <Input
                value={articleQuantites[a.key] || ""}
                onChange={(e) => updateArticleQ(a.key, e.target.value)}
                placeholder="0"
                inputMode="decimal"
                className="w-full sm:w-20 text-right"
                data-testid={`or-parc-article-${a.key}`}
                style={{ fontSize: 16 }}
              />
            </div>
          ))}
        </div>
      </Card>

      {/* iter121 — Articles libres (Plaquettes, Disques, etc.) */}
      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Autres articles</h2>
          <Button size="sm" variant="outline" onClick={addArticleLibre} data-testid="or-parc-add-libre">
            <Plus className="w-4 h-4 mr-1" /> Ajouter
          </Button>
        </div>
        <p className="text-xs text-gray-500">Ex : Plaquettes avant, Disques arrière, Balais essuie-glace, etc.</p>
        {articlesLibres.length === 0 ? (
          <div className="text-[11px] text-gray-400 italic">Aucun article libre. Clique « Ajouter » pour en saisir.</div>
        ) : (
          <div className="space-y-2">
            {articlesLibres.map((a, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <Input
                  value={a.libelle}
                  onChange={(e) => updateArticleLibre(i, "libelle", e.target.value)}
                  placeholder="ex: Plaquettes avant"
                  className="flex-1 min-w-[160px]"
                  data-testid={`or-parc-libre-libelle-${i}`}
                  style={{ fontSize: 16 }}
                />
                <Input
                  value={a.quantite}
                  onChange={(e) => updateArticleLibre(i, "quantite", e.target.value)}
                  placeholder="qté"
                  inputMode="decimal"
                  className="w-20 text-right"
                  data-testid={`or-parc-libre-quantite-${i}`}
                  style={{ fontSize: 16 }}
                />
                <select
                  value={a.unite}
                  onChange={(e) => updateArticleLibre(i, "unite", e.target.value)}
                  className="w-20 px-2 py-2 border border-gray-300 rounded-md text-sm bg-white"
                  data-testid={`or-parc-libre-unite-${i}`}
                  style={{ fontSize: 16 }}
                >
                  <option value="piece">pce</option>
                  <option value="paire">paire</option>
                  <option value="kit">kit</option>
                  <option value="litre">litre</option>
                  <option value="kg">kg</option>
                  <option value="m">m</option>
                </select>
                <button onClick={() => removeArticleLibre(i)} className="p-2 text-red-500 hover:bg-red-50 rounded" data-testid={`or-parc-libre-remove-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Main d'œuvre */}
      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Main-d&apos;œuvre</h2>
          <Button size="sm" variant="outline" onClick={addMoLine} data-testid="or-parc-add-mo">
            <Plus className="w-4 h-4 mr-1" /> Ajouter
          </Button>
        </div>
        <p className="text-xs text-gray-500">Saisis le libellé et le temps en heures (la virgule est acceptée : 0,5 = 0.5).</p>
        <div className="space-y-2">
          {moLignes.map((ln, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <Input
                value={ln.libelle}
                onChange={(e) => updateMoLine(i, "libelle", e.target.value)}
                placeholder="ex: Vidange moteur"
                className="flex-1 min-w-[160px]"
                data-testid={`or-parc-mo-libelle-${i}`}
                style={{ fontSize: 16 }}
              />
              <Input
                value={ln.heures}
                onChange={(e) => updateMoLine(i, "heures", e.target.value)}
                placeholder="0,5"
                inputMode="decimal"
                className="w-24 text-right"
                data-testid={`or-parc-mo-heures-${i}`}
                style={{ fontSize: 16 }}
              />
              <button onClick={() => removeMoLine(i)} className="p-2 text-red-500 hover:bg-red-50 rounded" disabled={moLignes.length <= 1} data-testid={`or-parc-mo-remove-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </Card>

      {/* Notes */}
      <Card className="p-5 space-y-2">
        <Label className="text-xs">Notes internes (facultatif)</Label>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="or-parc-notes" style={{ fontSize: 16 }} />
      </Card>

      <div className="sticky bottom-4 bg-white/90 backdrop-blur border border-gray-200 rounded-2xl p-4 flex flex-wrap items-center justify-end gap-2 shadow-lg">
        <Button variant="outline" onClick={() => navigate("/atelier")}>Annuler</Button>
        <Button
          onClick={() => handleSubmit("commencement")}
          disabled={!canSubmit || submitting}
          variant="outline"
          className="border-[#52a675] text-[#52a675] hover:bg-[#52a675]/10"
          data-testid="or-parc-submit-encours"
        >
          {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Créer (en travaux)
        </Button>
        <Button
          onClick={() => handleSubmit("travaux_effectue")}
          disabled={!canSubmit || submitting}
          className="bg-[#52a675] hover:bg-[#3d8a5c]"
          data-testid="or-parc-submit-termine"
        >
          {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Créer et terminer
        </Button>
      </div>
    </div>
  );
}
